import type { RxCollection } from 'rxdb';
import type {
    DiaryDocType,
    ProjectDocType,
    TranslationDocType,
    TranslationLanguage,
    TranslationOwnerType
} from '@ast-data/shared';
import {
    deliverableText,
    detectLanguage,
    isStale,
    looksForeign,
    translationStatus,
    type TranslationContent
} from './translationState';
import { familyProjectIds } from '../projects/family';

/**
 * Deliverables (PDF, ExcaBook) are written in German. There is no per-project
 * Abgabesprache (yet): every export asks for German.
 */
export const DELIVERABLE_LANGUAGE: TranslationLanguage = 'de';

/**
 * Why a text goes into a deliverable as its original although that is not
 * German: its German translation is not accepted yet, was accepted for a text
 * the field no longer holds, or there is none.
 */
export type DeliverableGapReason = 'unapproved' | 'stale' | 'untranslated';

export interface DeliverableGap {
    ownerType: TranslationOwnerType;
    ownerId: string;
    field: string;
    reason: DeliverableGapReason;
    /**
     * The record as exported — what the warning names it by and links it with
     * (its number, its project, a description's Befund).
     */
    record: Readonly<Record<string, unknown>>;
}

/** The prose of a Tagebuch day — every one of them is printed and translatable. */
export const DIARY_TEXT_FIELDS: readonly (keyof DiaryDocType & string)[] = [
    'dailyCourse',
    'topsoilRemoval',
    'surveying',
    'cleaning',
    'cuttingFindings',
    'drawing',
    'descriptions',
    'recovery',
    'finds',
    'photos',
    'excavatorNote'
];

/** Below this, a text without translation is too short to call foreign. */
const UNTRANSLATED_MIN_LENGTH = 30;

/**
 * Whether a field's text is a gap in a German deliverable — null when it goes
 * in as German: empty, an accepted German translation, or an original that is
 * German itself (it was translated INTO the other language, or reads so).
 *
 * Without a translation the language is only guessed, and only a long text that
 * plainly reads English or a third language counts: a short German keyword list
 * must not set off a warning.
 */
export function deliverableGap(
    original: string,
    translation: TranslationContent | null
): DeliverableGapReason | null {
    const trimmed = original.trim();
    if (trimmed === '') {
        return null;
    }
    if (translation && translation.targetLang === DELIVERABLE_LANGUAGE) {
        const status = translationStatus(translation, original);
        if (status === 'approved') {
            return null;
        }
        return status === 'stale' ? 'stale' : 'unapproved';
    }
    // A translation into the other language says what the original was.
    if (translation && translation.sourceLang !== DELIVERABLE_LANGUAGE) {
        return 'untranslated';
    }
    if (translation && !isStale(translation, original)) {
        return null;
    }
    // Translated from German, but changed since: the text it was made from is
    // German, what came after is not vouched for. Only THAT is looked at — a
    // Polish sentence dictated after a German paragraph would pass as German
    // in the whole.
    return readsForeign(
        translation ? addedSince(translation.sourceText, original) : trimmed
    )
        ? 'untranslated'
        : null;
}

/**
 * Whether a text without a translation is plainly not German. Only a long text
 * that reads English or a third language counts: a short German keyword list
 * must not set off a warning.
 */
function readsForeign(text: string): boolean {
    const trimmed = text.trim();
    if (trimmed.length < UNTRANSLATED_MIN_LENGTH) {
        return false;
    }
    return detectLanguage(trimmed) === 'en' || looksForeign(trimmed);
}

/** What `now` holds beyond `before`: without their common start and end. */
export function addedSince(before: string, now: string): string {
    let start = 0;
    while (
        start < before.length &&
        start < now.length &&
        before[start] === now[start]
    ) {
        start += 1;
    }
    let end = 0;
    while (
        end < before.length - start &&
        end < now.length - start &&
        before[before.length - 1 - end] === now[now.length - 1 - end]
    ) {
        end += 1;
    }
    return now.slice(start, now.length - end);
}

/**
 * The long texts a deliverable writes, by record kind — one list for every
 * export, so a field printed in one is translated (and reported) in all.
 * Sample and drawing-sheet texts go into no deliverable.
 */
export const DELIVERABLE_FIELDS: Partial<
    Record<TranslationOwnerType, readonly string[]>
> = {
    project: ['description'],
    finding: ['remark'],
    findingPlanum: ['description', 'interpretation'],
    findingProfile: ['description', 'interpretation'],
    overallDescription: ['description', 'remark'],
    areaDescription: [
        'conditionAtRecording',
        'naturalSoil',
        'findingsOverview'
    ],
    find: ['label', 'remark', 'findDescription'],
    photo: ['motif', 'remarks'],
    diary: DIARY_TEXT_FIELDS
};

/**
 * The way back, for an ExcaBook file the app exported and imports again: the
 * export wrote a field's ACCEPTED German translation in place of its original,
 * so a cell that comes back holding exactly that German is the translation,
 * not a new text — the original stays. Anything else in the cell (an edit made
 * in ExcaBook) is taken as usual.
 */
export function keepOriginalsOverDelivered<T extends Record<string, unknown>>(
    ownerType: TranslationOwnerType,
    content: T,
    previous: Record<string, unknown>,
    translations: ReadonlyMap<string, TranslationContent>
): T {
    const merged: Record<string, unknown> = { ...content };
    for (const field of DELIVERABLE_FIELDS[ownerType] ?? []) {
        const incoming = content[field];
        const original = previous[field];
        const translation = translations.get(field);
        if (
            typeof incoming !== 'string' ||
            typeof original !== 'string' ||
            !translation
        ) {
            continue;
        }
        const delivered = deliverableText(
            original,
            translation,
            DELIVERABLE_LANGUAGE
        );
        if (delivered !== original && incoming.trim() === delivered.trim()) {
            merged[field] = original;
        }
    }
    return merged as T;
}

/**
 * The texts of ONE export run: records go in with their originals and come out
 * with the accepted German translations in their place, and everything that
 * stays an original although it is not German is noted in `gaps` — for the
 * question before the export (see confirmTranslationGaps).
 */
export interface DeliverableTexts {
    /** `docs` with their DELIVERABLE_FIELDS in deliverable German. */
    apply<T extends { id: string }>(
        ownerType: TranslationOwnerType,
        docs: readonly T[]
    ): Promise<T[]>;
    readonly gaps: readonly DeliverableGap[];
}

/** The project a record belongs to — for a project, itself. */
function scopeOf(ownerType: TranslationOwnerType, doc: { id: string }): string {
    if (ownerType === 'project') {
        return doc.id;
    }
    return (doc as { projectId?: string }).projectId ?? '';
}

function contentOf(doc: TranslationDocType): TranslationContent {
    return {
        sourceLang: doc.sourceLang,
        targetLang: doc.targetLang,
        sourceText: doc.sourceText,
        text: doc.text,
        origin: doc.origin,
        approvedBy: doc.approvedBy,
        approvedAt: doc.approvedAt
    };
}

/**
 * Without a collection (a test, a database still opening) every text stays the
 * original and nothing is reported.
 *
 * A translation counts for a record when it is filed under the record's kind
 * and under a project of the record's FAMILY: a shared Befund is opened — and
 * translated — from any Teilmaßnahme, and its translation is filed under that
 * one (see useRecordTranslations). A record has one translation per field
 * whichever phase wrote it, so there is nothing to choose between.
 */
export function createDeliverableTexts(
    translations: RxCollection<TranslationDocType> | null,
    projects: RxCollection<ProjectDocType> | null
): DeliverableTexts {
    const gaps: DeliverableGap[] = [];
    // Loaded per project, once, and indexed by kind, record and field: a
    // whole-project ExcaBook names thousands of records in eight calls.
    const loaded = new Map<string, Promise<Map<string, TranslationDocType>>>();
    const keyOf = (ownerType: string, ownerId: string, field: string) =>
        `${ownerType}\u0000${ownerId}\u0000${field}`;
    let allProjects: Promise<ProjectDocType[]> | null = null;

    function projectTranslations(projectId: string) {
        let pending = loaded.get(projectId);
        if (!pending) {
            pending = translations
                ? translations
                      .find({ selector: { projectId } })
                      .exec()
                      .then(
                          (docs) =>
                              new Map(
                                  docs.map((doc) => {
                                      const json =
                                          doc.toJSON() as TranslationDocType;
                                      return [
                                          keyOf(
                                              json.ownerType,
                                              json.ownerId,
                                              json.field
                                          ),
                                          json
                                      ];
                                  })
                              )
                      )
                : Promise.resolve(new Map());
            loaded.set(projectId, pending);
        }
        return pending;
    }

    async function familyOf(projectId: string): Promise<string[]> {
        allProjects ??= projects
            ? projects
                  .find()
                  .exec()
                  .then((docs) =>
                      docs.map((doc) => doc.toJSON() as ProjectDocType)
                  )
            : Promise.resolve([]);
        const known = await allProjects;
        const self = known.find((project) => project.id === projectId);
        return familyProjectIds(known, self?.familyId ?? '', projectId);
    }

    async function apply<T extends { id: string }>(
        ownerType: TranslationOwnerType,
        docs: readonly T[]
    ): Promise<T[]> {
        const fields = DELIVERABLE_FIELDS[ownerType] ?? [];
        if (!translations || fields.length === 0) {
            return [...docs];
        }
        // Each record's family, and every family project's index, once —
        // fetched side by side.
        const scopes = [...new Set(docs.map((doc) => scopeOf(ownerType, doc)))];
        const families = new Map(
            await Promise.all(
                scopes.map(
                    async (scope) => [scope, await familyOf(scope)] as const
                )
            )
        );
        const projectIds = [...new Set([...families.values()].flat())];
        const indexes = new Map(
            await Promise.all(
                projectIds.map(
                    async (projectId) =>
                        [
                            projectId,
                            await projectTranslations(projectId)
                        ] as const
                )
            )
        );
        /** The record's translation of a field, filed in its family. */
        const translationOf = (doc: T, field: string) => {
            const key = keyOf(ownerType, doc.id, field);
            for (const projectId of families.get(scopeOf(ownerType, doc)) ??
                []) {
                const stored = indexes.get(projectId)?.get(key);
                if (stored) {
                    return contentOf(stored);
                }
            }
            return null;
        };
        return docs.map((doc) => {
            const delivered: Record<string, unknown> = { ...doc };
            for (const field of fields) {
                const original = String(
                    (doc as Record<string, unknown>)[field] ?? ''
                );
                const translation = translationOf(doc, field);
                const reason = deliverableGap(original, translation);
                if (reason) {
                    gaps.push({
                        ownerType,
                        ownerId: doc.id,
                        field,
                        reason,
                        record: doc
                    });
                }
                const text = deliverableText(
                    original,
                    translation,
                    DELIVERABLE_LANGUAGE
                );
                if (text !== original) {
                    delivered[field] = text;
                }
            }
            return delivered as T;
        });
    }

    return { apply, gaps };
}
